import React from "react";
import { Spinner, Modal, Button } from "react-bootstrap";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import * as Cookies from "es-cookie";
import * as XLSX from "xlsx";
import { parseWaTemplate } from "../../utils/waTemplateParser";
import NotifyAttendanceStudentsModal from "./NotifyAttendanceStudentsModal";
import NotifyStudentModal from "./NotifyStudentModal";
import {
  getAuthorizedBranchIds,
  getAuthorizedClassIds,
  getAuthorizedSectionIds,
} from "../../utils/authHelper";

// ── Backend REST base. Derived from REACT_APP_API_URL (same backend as GraphQL,
//    set per-branch by the deploy) so the live site never calls http://localhost.
//    Strips the trailing "/graphql"; falls back to localhost:3008 for local dev. ──
const BACKEND = (
  process.env.REACT_APP_API_URL || "http://localhost:3008/graphql"
).replace(/\/graphql\/?$/, "");
const REST = `${BACKEND}/api/v1/attendance`;

// ── Eligibility bands (the 75% rule — configurable) ──
const ELIGIBLE_MIN = 75; // ≥ → Eligible
const CONDONABLE_MIN = 65; // ≥ → Shortage (condonable); < → Detained
const statusOf = (pct) =>
  pct >= ELIGIBLE_MIN
    ? { key: "eligible", label: "Eligible", c: "#15803d", bg: "#f0fdf4" }
    : pct >= CONDONABLE_MIN
      ? { key: "shortage", label: "Shortage", c: "#b45309", bg: "#fffbeb" }
      : { key: "detained", label: "Detained", c: "#dc2626", bg: "#fef2f2" };

const heatColor = (pct) => {
  if (pct == null) return "#f1f5f9"; // no school
  if (pct >= 90) return "#16a34a";
  if (pct >= 75) return "#4ade80";
  if (pct >= 60) return "#facc15";
  if (pct >= 40) return "#fb923c";
  return "#ef4444";
};

const COLLEGE = Cookies.get("institutionname") || "your college";

// Client-side page size for the Defaulters / At-Risk tables.
const PAGE_SIZE = 25;

const S = {
  page: { background: "linear-gradient(135deg,#f0f4ff 0%,#faf5ff 100%)", borderRadius: 18, padding: "20px 22px", minHeight: 400 },
  bar: { display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 18 },
  lbl: { fontSize: 11, fontWeight: 800, color: "#94a3b8", marginBottom: 4, display: "block", letterSpacing: 0.3 },
  sel: { borderRadius: 10, border: "1.5px solid #e5e7eb", padding: "8px 12px", fontSize: 13, background: "#fff", color: "#1f2937", minWidth: 150 },
  kpiRow: { display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 18 },
  kpi: { flex: "1 1 150px", background: "#fff", borderRadius: 14, padding: "14px 16px", boxShadow: "0 2px 12px rgba(0,0,0,0.05)", borderTop: "4px solid #6366f1" },
  kpiVal: { fontSize: 24, fontWeight: 800 },
  kpiLbl: { fontSize: 12, color: "#64748b", fontWeight: 600 },
  tabs: { display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" },
  tab: { padding: "8px 16px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", border: "1.5px solid #e5e7eb", background: "#fff", color: "#475569" },
  tabOn: { background: "#4f46e5", color: "#fff", borderColor: "#4f46e5" },
  card: { background: "#fff", borderRadius: 16, padding: "18px 20px", boxShadow: "0 2px 16px rgba(0,0,0,0.06)", marginBottom: 18 },
  th: { fontSize: 11, fontWeight: 800, color: "#94a3b8", padding: "8px 10px", textAlign: "left", whiteSpace: "nowrap", borderBottom: "2px solid #f1f5f9" },
  td: { fontSize: 13, padding: "9px 10px", borderBottom: "1px solid #f1f5f9", color: "#1e293b" },
  pill: (c, bg) => ({ background: bg, color: c, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 800, whiteSpace: "nowrap" }),
  btn: (bg) => ({ background: bg, color: "#fff", border: "none", borderRadius: 8, padding: "6px 12px", fontWeight: 700, fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }),
  ghost: { background: "#fff", color: "#4f46e5", border: "1.5px solid #4f46e5", borderRadius: 8, padding: "6px 12px", fontWeight: 700, fontSize: 12, cursor: "pointer" },
  notice: (ok) => ({ padding: "10px 14px", borderRadius: 10, marginBottom: 14, fontSize: 13, fontWeight: 600, background: ok ? "#eff6ff" : "#fef2f2", color: ok ? "#1d4ed8" : "#dc2626", border: `1px solid ${ok ? "#bfdbfe" : "#fecaca"}` }),
};

// Approved WhatsApp templates (c3crm.wa_template) — the same library the
// Communication Management → WhatsApp → Templates tab lists. Reused here so an
// admin can pick an approved template when alerting at-risk parents instead of
// being locked to the backend's single env template.
const GET_WA_TEMPLATES = gql`
  query getWaTemplatesByInstitute($institute_id: Int) {
    getWaTemplatesByInstitute(institute_id: $institute_id) {
      id
      type
      template_name
      template_category
      template_lang
      template_form
      template_header
      image_size
      status
    }
  }
`;

const GET_ACADEMIC_YEARS = gql`query getAcademicYears($id: Int) { getAcademicYears(id: $id) { id academic_year } }`;
const GET_BRANCHES = gql`query getBranches($institution_id: Int!) { getBranches(institution_id: $institution_id) { id branch_name sections { id class_id section_name } } }`;
const GET_CLASSES = gql`query($id: Int) { getExamClasses(id: $id) { id class } }`;
const GET_MONTHLY = gql`
  query getMonthlyAttendanceReport($academic_year_id: Int!, $institute_id: Int!, $branch_id: Int, $branch_ids: String, $class_id: Int!, $section_id: String, $month: String) {
    getMonthlyAttendanceReport(academic_year_id: $academic_year_id, institute_id: $institute_id, branch_id: $branch_id, branch_ids: $branch_ids, class_id: $class_id, section_id: $section_id, month: $month) {
      student_id admission_no student_name student_mobile roll_no class_id section_id branch_id
      total_present total_absent total_working_days percentage father_name father_phone mother_phone
      days { day status }
    }
  }`;

// Message Analysis tab: attendance campaigns (both channels) + per-campaign recipients.
// SMS stats come from education_mngt.sms_outbox; WhatsApp from c3crm.outbond_campaign +
// outbond_campaign_stats — merged by the getAttendanceCampaigns resolver.
const GET_ATTENDANCE_CAMPAIGNS = gql`
  query($institute_id: Int!, $branch_id: Int, $date: String) {
    getAttendanceCampaigns(institute_id: $institute_id, branch_id: $branch_id, date: $date) {
      id campaign_name channel notification_type inserted_time
      attempted sent delivered read failed scheduled cancelled
    }
  }
`;
const GET_CAMPAIGN_RECIPIENTS = gql`
  query($id: Int!, $channel: String!) {
    getCampaignRecipients(id: $id, channel: $channel) {
      mobile name status sent_at error message
    }
  }
`;

const instId = () => parseInt(Cookies.get("institutionid"));
const curMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const monthOptions = () => {
  const out = []; const d = new Date(); const y = d.getFullYear();
  for (let m = 0; m <= d.getMonth(); m++) out.push({ value: `${y}-${String(m + 1).padStart(2, "0")}`, label: new Date(y, m).toLocaleString("default", { month: "long" }) + ` ${y}` });
  return out.reverse();
};

class AttendanceCommandCenter extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      years: [], branches: [], classMap: {},
      year: "", branch: "", month: curMonth(), threshold: ELIGIBLE_MIN,
      // Start in loading so the first render shows a spinner, not "no data".
      students: [], loading: true, error: "",
      view: "board", search: "", selected: {}, page: 1,
      // Message Analysis tab
      campaigns: [], campaignsLoading: false, campaignChannel: "all",
      campaignSearch: "", campaignPage: 1,
      drillCampaign: null, drillRows: [], drillLoading: false, drillSearch: "", drillPage: 1,
      wa: { configured: false }, sending: false, notice: null,
      // Approved-template library + the "pick a template & edit params" modal.
      waTemplates: [], waTemplatesLoading: false,
      tplModal: null, // { rows, buildDetail, templateId, params: [..] }
      notifyModal: false, // attendance "Notify Students" campaign modal
      // Test-number redirect removed from the UI. Kept as a constant "" so testTo() and the
      // downstream send paths stay inert (no redirect) without touching their wiring.
      testNumber: "",
      notifyStudent: null, // { student, channel } — single-student template notify
    };
    // Guards against stale responses when filters change quickly.
    this._loadToken = 0;
  }

  componentDidMount() {
    // Wipe any test number a prior session persisted — the UI to clear it is gone, so a
    // leftover value would silently redirect every real send.
    try { localStorage.removeItem("rizee_cc_test_number"); } catch (e) { /* ignore */ }
    this.loadMeta(); this.loadWaStatus(); this.loadWaTemplates();
  }

  // The page-header branch dropdown drives this screen. When it changes, adopt the new
  // branch and reload — this is the only way branch changes now that the local select is gone.
  componentDidUpdate(prevProps) {
    // React to the header changing EITHER the single branch or the multi-select set. branchIds
    // is the authority when present (all/several branches); branchId is the single-pick case.
    const branchIdsChanged = prevProps.branchIds !== this.props.branchIds;
    const branchIdChanged = prevProps.branchId !== this.props.branchId;
    if (branchIdsChanged || branchIdChanged) {
      // Keep state.branch as the FIRST selected branch (single-branch features like notify /
      // campaigns still key off it); loadData aggregates across the whole scope.
      const ids = this.scopeBranchIds();
      const first = ids.length ? ids[0] : this.state.branch;
      this.setState({ branch: first || "" }, this.loadData);
    }
  }

  loadWaStatus = async () => {
    try { const r = await fetch(`${REST}/whatsapp/status`); if (r.ok) this.setState({ wa: await r.json() }); } catch (_) {}
  };

  // Pull the institute's approved WhatsApp templates (only APPROVED ones are
  // deliverable by Meta, so other statuses are filtered out for the picker).
  loadWaTemplates = async () => {
    this.setState({ waTemplatesLoading: true });
    try {
      const res = await this.props.client.query({
        query: GET_WA_TEMPLATES,
        variables: { institute_id: instId() },
        fetchPolicy: "cache-first",
      });
      const raw = (res.data && res.data.getWaTemplatesByInstitute) || [];
      const templates = raw
        .map((t) => {
          const parsed = parseWaTemplate(t);
          return {
            id: t.id,
            name: t.template_name || "Untitled",
            lang: t.template_lang || "en",
            status: t.status || "Pending",
            body: parsed.body || "",
            paramCount: parsed.paramCount || 0,
          };
        })
        .filter((t) => String(t.status).toLowerCase() === "approved");
      this.setState({ waTemplates: templates, waTemplatesLoading: false });
    } catch (_) {
      this.setState({ waTemplatesLoading: false });
    }
  };

  loadMeta = async () => {
    try {
      const [yRes, bRes, cRes] = await Promise.all([
        this.props.client.query({ query: GET_ACADEMIC_YEARS, variables: { id: 1 }, fetchPolicy: "network-only" }),
        this.props.client.query({ query: GET_BRANCHES, variables: { institution_id: instId() }, fetchPolicy: "network-only" }),
        this.props.client.query({ query: GET_CLASSES, variables: { id: instId() }, fetchPolicy: "network-only" }),
      ]);
      const years = yRes.data.getAcademicYears || [];
      let branches = bRes.data.getBranches || [];
      const authBranchIds = getAuthorizedBranchIds();
      const authClassIds = getAuthorizedClassIds();
      const authSecIds = getAuthorizedSectionIds();

      if (authBranchIds.length) {
        branches = branches.filter((b) => authBranchIds.includes(String(b.id)));
      }

      branches = branches.map((b) => {
        let sec = b.sections || [];
        if (authClassIds.length) {
          sec = sec.filter((s) => authClassIds.includes(String(s.class_id)));
        }
        if (authSecIds.length) {
          sec = sec.filter((s) => authSecIds.includes(String(s.id)));
        }
        return { ...b, sections: sec };
      });
      const classMap = {};
      (cRes.data.getExamClasses || []).forEach((c) => { classMap[c.id] = c.class; classMap[String(c.id)] = c.class; });
      // Branch is driven by the page-header dropdown (props.branchId), not a local select.
      // Fall back to the first branch only when the header hasn't picked one.
      const branch = this.props.branchId != null && this.props.branchId !== ""
        ? this.props.branchId
        : (branches[0] ? branches[0].id : "");
      this.setState(
        { years, branches, classMap, year: years[0] ? years[0].id : "", branch },
        this.loadData,
      );
    } catch (e) { this.setState({ error: e.message, loading: false }); }
  };

  // Branch ids in scope: props.branchIds is the header's multi-select CSV ("359,370"); when
  // several branches are picked (or all), the card must aggregate across ALL of them, not
  // just the first. Falls back to the single state.branch.
  scopeBranchIds = () => {
    const csv = this.props.branchIds;
    const authBranchIds = getAuthorizedBranchIds();
    let ids = [];
    if (csv != null && String(csv).trim() !== "") {
      ids = String(csv).split(",").map((x) => parseInt(x.trim())).filter(Boolean);
    } else if (this.state.branch) {
      ids = [parseInt(this.state.branch)];
    }

    if (authBranchIds.length > 0) {
      ids = ids.filter((id) => authBranchIds.includes(String(id)));
    }
    return ids;
  };

  loadData = async () => {
    const { year, month, branches } = this.state;
    const branchIds = this.scopeBranchIds();
    if (!year || !branchIds.length) { this.setState({ loading: false }); return; }
    // Union of classes across every selected branch's sections.
    const scoped = branches.filter((b) => branchIds.includes(+b.id));
    const classIds = [...new Set(scoped.flatMap((b) => (b.sections || []).map((s) => +s.class_id)))].filter(Boolean);
    if (!classIds.length) { this.setState({ students: [], loading: false, error: "" }); return; }
    const branchCsv = branchIds.join(",");
    const token = ++this._loadToken;
    this.setState({ loading: true, error: "", selected: {}, page: 1 });
    try {
      let anyError = false;
      // One query per class, scoped to all selected branches via branch_ids.
      const results = await Promise.all(
        classIds.map((cid) =>
          this.props.client.query({
            query: GET_MONTHLY,
            variables: { academic_year_id: parseInt(year), institute_id: instId(), branch_id: null, branch_ids: branchCsv, class_id: cid, section_id: "", month },
            fetchPolicy: "no-cache",
          }).then((r) => r.data.getMonthlyAttendanceReport || []).catch(() => { anyError = true; return []; }),
        ),
      );
      if (token !== this._loadToken) return; // a newer load superseded this one
      const studentsRaw = results.flat().map((s) => ({ ...s, pct: parseFloat(s.percentage) || 0 }));

      const authBranchIds = getAuthorizedBranchIds();
      const authClassIds = getAuthorizedClassIds();
      const authSecIds = getAuthorizedSectionIds();

      const students = studentsRaw.filter((s) => {
        if (authBranchIds.length > 0 && s.branch_id && !authBranchIds.includes(String(s.branch_id))) return false;
        if (authClassIds.length > 0 && s.class_id && !authClassIds.includes(String(s.class_id))) return false;
        if (authSecIds.length > 0 && s.section_id && !authSecIds.includes(String(s.section_id))) return false;
        return true;
      });

      this.setState({
        students,
        loading: false,
        // Distinguish a real "no data" from a failed/timed-out load.
        error: students.length === 0 && anyError ? "Couldn't load attendance — the server/DB looks busy. Tap ↻ Refresh to retry." : "",
      });
    } catch (e) {
      if (token !== this._loadToken) return;
      this.setState({ error: e.message, loading: false });
    }
  };

  // ── Message Analysis ──
  loadCampaigns = async () => {
    this.setState({ campaignsLoading: true, drillCampaign: null, drillRows: [] });
    try {
      const res = await this.props.client.query({
        query: GET_ATTENDANCE_CAMPAIGNS,
        // No date → every attendance campaign for the institute; branch scopes WhatsApp.
        variables: { institute_id: instId(), branch_id: parseInt(this.state.branch) || null, date: null },
        fetchPolicy: "network-only",
      });
      let campaigns = (res.data && res.data.getAttendanceCampaigns) || [];
      const authBranchIds = getAuthorizedBranchIds();
      if (authBranchIds.length > 0) {
        campaigns = campaigns.filter((c) => !c.branch_id || authBranchIds.includes(String(c.branch_id)));
      }
      this.setState({ campaigns, campaignsLoading: false });
    } catch (e) {
      this.setState({ campaigns: [], campaignsLoading: false });
    }
  };

  openDrill = async (c) => {
    this.setState({ drillCampaign: c, drillRows: [], drillLoading: true, drillSearch: "", drillPage: 1 });
    try {
      const res = await this.props.client.query({
        query: GET_CAMPAIGN_RECIPIENTS,
        variables: { id: c.id, channel: c.channel },
        fetchPolicy: "network-only",
      });
      let rows = (res.data && res.data.getCampaignRecipients) || [];
      const authBranchIds = getAuthorizedBranchIds();
      const authClassIds = getAuthorizedClassIds();
      const authSecIds = getAuthorizedSectionIds();

      rows = rows.filter((r) => {
        if (authBranchIds.length > 0 && r.branch_id && !authBranchIds.includes(String(r.branch_id))) return false;
        if (authClassIds.length > 0 && r.class_id && !authClassIds.includes(String(r.class_id))) return false;
        if (authSecIds.length > 0 && r.section_id && !authSecIds.includes(String(r.section_id))) return false;
        return true;
      });
      this.setState({ drillRows: rows, drillLoading: false });
    } catch (e) {
      this.setState({ drillRows: [], drillLoading: false });
    }
  };

  // ── derived ──
  sectionName = (id) => {
    for (const b of this.state.branches) { const s = (b.sections || []).find((x) => +x.id === +id); if (s) return s.section_name; }
    return id;
  };
  className = (id) => this.state.classMap[id] || this.state.classMap[String(id)] || id;

  recentConsecutiveAbsences = (days) => {
    let c = 0;
    for (let i = (days || []).length - 1; i >= 0; i--) {
      const st = days[i].status;
      if (st === "A") c++;
      else if (st === "P") break;
      // H / SUNDAY / NM → skip (non-working day)
    }
    return c;
  };

  todayAbsent = (s) => {
    const dd = String(new Date().getDate()).padStart(2, "0");
    const mm = String(new Date().getMonth() + 1).padStart(2, "0");
    const rec = (s.days || []).find((d) => d.day === `${dd}-${mm}` || d.day === dd);
    return rec && rec.status === "A";
  };

  // ── WhatsApp ──
  // Default body params used for the backend's env template AND as the
  // pre-fill when an admin picks an approved template in the picker.
  defaultParams = (s, buildDetail) => [
    s.student_name || `Adm ${s.admission_no}`,
    buildDetail(s),
    COLLEGE,
  ];

  // Entry point for every "Alert / Notify" button. If the institute has approved
  // templates, open the picker so the admin chooses a template + edits its params;
  // otherwise fall back to the backend's single env template (legacy behaviour).
  sendWhatsApp = (rows, buildDetail) => {
    if (!this.state.wa.configured) {
      this.setState({ notice: { ok: false, msg: "WhatsApp isn't configured on the backend yet. Add WHATSAPP_TOKEN + WHATSAPP_PHONE_NUMBER_ID and create the approved template." } });
      return;
    }
    if (this.state.waTemplates.length > 0) {
      this.openTemplatePicker(rows, buildDetail);
      return;
    }
    this.dispatchWhatsApp(rows, buildDetail, null);
  };

  // Open the picker. Default to the first approved template and pre-fill its
  // params from the (first) student row so the admin sees a ready-to-send draft.
  openTemplatePicker = (rows, buildDetail) => {
    const first = this.state.waTemplates[0];
    this.setState({
      tplModal: {
        rows,
        buildDetail,
        templateId: first ? first.id : null,
        params: this.paramsForTemplate(first, rows[0], buildDetail),
      },
    });
  };

  // Build the editable param list for a template, pre-filled positionally from
  // the default attendance params (name, detail, college), padded/trimmed to the
  // template's placeholder count.
  paramsForTemplate = (tpl, sampleRow, buildDetail) => {
    if (!tpl) return [];
    const defaults = sampleRow ? this.defaultParams(sampleRow, buildDetail) : [];
    return Array.from({ length: tpl.paramCount }, (_, i) => defaults[i] || "");
  };

  selectTemplate = (templateId) => {
    const tpl = this.state.waTemplates.find((t) => String(t.id) === String(templateId));
    this.setState((s) => ({
      tplModal: s.tplModal && {
        ...s.tplModal,
        templateId,
        params: this.paramsForTemplate(tpl, s.tplModal.rows[0], s.tplModal.buildDetail),
      },
    }));
  };

  updateTplParam = (idx, value) =>
    this.setState((s) => ({
      tplModal: s.tplModal && {
        ...s.tplModal,
        params: s.tplModal.params.map((p, i) => (i === idx ? value : p)),
      },
    }));

  // Confirm the picker → send using the chosen template, applying the admin's
  // edited params to every recipient. Per-row param[0] is still personalised to
  // each student's name (the picker pre-fills from the first row only).
  confirmTemplateSend = () => {
    const { tplModal, waTemplates } = this.state;
    if (!tplModal) return;
    const tpl = waTemplates.find((t) => String(t.id) === String(tplModal.templateId));
    if (!tpl) { this.setState({ notice: { ok: false, msg: "Pick a template first." } }); return; }
    this.dispatchWhatsApp(tplModal.rows, tplModal.buildDetail, {
      name: tpl.name,
      lang: tpl.lang,
      editedParams: tplModal.params,
      paramCount: tpl.paramCount,
    });
    this.setState({ tplModal: null });
  };

  // Actually POST to /whatsapp/send. `chosen` is null for the legacy env-template
  // path, or { name, lang, editedParams, paramCount } for a picked template.
  // The test number only takes effect once it's a plausible mobile (≥10 digits)
  // so a half-typed number can never hijack a real send.
  testTo = () => {
    const t = (this.state.testNumber || "").trim();
    return t.length >= 10 ? t : "";
  };

  dispatchWhatsApp = async (rows, buildDetail, chosen) => {
    const override = this.testTo();
    const messages = rows
      .map((s) => {
        const to = override || s.father_phone || s.mother_phone || s.student_mobile;
        if (!to) return null;
        let params;
        if (chosen) {
          // Start from the admin's edited params, but re-personalise the first
          // placeholder to each student's name so a bulk send isn't all one name.
          params = chosen.editedParams.slice(0, chosen.paramCount);
          if (params.length > 0) params[0] = s.student_name || `Adm ${s.admission_no}`;
        } else {
          params = this.defaultParams(s, buildDetail);
        }
        return { to, edu_student_id: s.student_id, att_date: this.state.month, params };
      })
      .filter(Boolean);
    if (!messages.length) { this.setState({ notice: { ok: false, msg: "No parent phone numbers found for the selected students." } }); return; }
    if (this.state.sending) return; // guard against double-clicks
    // Bulk sends go to real parents — confirm first (test mode goes to us, not parents).
    if (!override && messages.length > 1 && !window.confirm(`Send a real WhatsApp message to ${messages.length} parents now?`)) return;
    if (override && !window.confirm(`🧪 TEST MODE — send ${messages.length} WhatsApp message(s) to ${override} (your test number)?`)) return;
    this.setState({ sending: true, notice: null });
    try {
      const body = { institute_id: instId(), messages };
      if (chosen) { body.template = chosen.name; body.language = chosen.lang; }
      const r = await fetch(`${REST}/whatsapp/send`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Send failed");
      this.setState({ sending: false, notice: { ok: data.failed === 0, msg: `${override ? `🧪 TEST → ${override} · ` : ""}WhatsApp: ${data.sent} sent${data.failed ? `, ${data.failed} failed` : ""} (of ${data.total}).` } });
    } catch (e) { this.setState({ sending: false, notice: { ok: false, msg: e.message } }); }
  };

  // ── Single-student notify (template-based SMS / WhatsApp modal) ──
  openNotifyStudent = (student, channel) => this.setState({ notifyStudent: { student, channel } });

  exportDefaulters = (rows) => {
    const data = rows.map((s, i) => ({
      "S.No": i + 1, Admission: s.admission_no, Roll: s.roll_no, Student: s.student_name,
      Class: this.className(s.class_id), Section: this.sectionName(s.section_id),
      "Working Days": s.total_working_days, Present: s.total_present, Absent: s.total_absent,
      "Attendance %": s.pct, Status: statusOf(s.pct).label, "Parent Phone": s.father_phone || s.mother_phone || s.student_mobile || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Defaulters");
    XLSX.writeFile(wb, `defaulters_${this.state.month}.xlsx`);
  };

  render() {
    const { years, branches, year, branch, month, threshold, students, loading, error, view, search, wa, sending, notice } = this.state;

    // Only judge students who actually have attendance days — a 0-working-day
    // student (section not yet on the device) isn't a "0% defaulter", just no data.
    const tracked = students.filter((s) => (s.total_working_days || 0) > 0);
    const noData = students.length - tracked.length;
    const eligible = tracked.filter((s) => s.pct >= ELIGIBLE_MIN).length;
    const shortage = tracked.filter((s) => s.pct >= CONDONABLE_MIN && s.pct < ELIGIBLE_MIN).length;
    const detained = tracked.filter((s) => s.pct < CONDONABLE_MIN).length;
    const avg = tracked.length ? Math.round(tracked.reduce((a, s) => a + s.pct, 0) / tracked.length) : 0;
    const absentToday = tracked.filter((s) => this.todayAbsent(s));
    // Search matches name / admission / roll. When a query is typed on the
    // Defaulters view it searches ALL tracked students (not just defaulters),
    // so a search always finds the student regardless of their percentage.
    const q = (search || "").trim().toLowerCase();
    const matchesSearch = (s) =>
      !q ||
      (s.student_name || "").toLowerCase().includes(q) ||
      String(s.admission_no || "").toLowerCase().includes(q) ||
      String(s.roll_no || "").toLowerCase().includes(q);
    // defaulterSet = the real below-threshold list (drives the bulk-notify /
    // export buttons); defaulters = what the Defaulters table displays, which
    // becomes an all-students search result while a query is typed.
    const defaulterSet = tracked.filter((s) => s.pct < threshold).sort((a, b) => a.pct - b.pct);
    const defaulters = q ? tracked.filter(matchesSearch).sort((a, b) => a.pct - b.pct) : defaulterSet;
    const atRisk = tracked
      .map((s) => ({ ...s, consec: this.recentConsecutiveAbsences(s.days) }))
      .filter((s) => s.pct < ELIGIBLE_MIN || s.consec >= 3)
      .filter(matchesSearch)
      .sort((a, b) => b.consec - a.consec || a.pct - b.pct);

    const KPIS = [
      { v: tracked.length, l: "Tracked Students", c: "#6366f1" },
      { v: `${avg}%`, l: "Avg Attendance", c: "#10b981" },
      { v: shortage, l: `Shortage (${CONDONABLE_MIN}–${ELIGIBLE_MIN}%)`, c: "#f59e0b" },
      { v: detained, l: `Detained (<${CONDONABLE_MIN}%)`, c: "#ef4444" },
      { v: absentToday.length, l: "Absent Today", c: "#dc2626" },
    ];

    return (
      <div style={S.page}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 22 }}>🛡️</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, color: "#1e293b" }}>Attendance Command Center</div>
            <div style={{ fontSize: 12, color: "#64748b" }}>Eligibility · defaulters · heatmap · at-risk · parent WhatsApp alerts</div>
          </div>
          {/* Only surface the badge when WhatsApp IS connected; the "not configured" state
              is noise on a screen that also sends SMS, so it's hidden. */}
          {wa.configured && (
            <span style={{ marginLeft: "auto", ...S.pill("#15803d", "#f0fdf4") }}>
              ● WhatsApp connected
              {this.state.waTemplates.length > 0 ? ` · ${this.state.waTemplates.length} template${this.state.waTemplates.length === 1 ? "" : "s"}` : ""}
            </span>
          )}
        </div>

        {/* filters */}
        <div style={S.bar}>
          <div>
            <label style={S.lbl}>ACADEMIC YEAR</label>
            <select style={S.sel} value={year} onChange={(e) => this.setState({ year: e.target.value }, this.loadData)}>
              {years.map((y) => <option key={y.id} value={y.id}>{y.academic_year}</option>)}
            </select>
          </div>
          {/* BRANCH is chosen by the page-header dropdown, so there's no local branch select. */}
          <div>
            <label style={S.lbl}>MONTH</label>
            <select style={S.sel} value={month} onChange={(e) => this.setState({ month: e.target.value }, this.loadData)}>
              {monthOptions().map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label style={S.lbl}>DEFAULTER BELOW</label>
            <select style={S.sel} value={threshold} onChange={(e) => this.setState({ threshold: parseInt(e.target.value) })}>
              {[85, 80, 75, 70, 65].map((t) => <option key={t} value={t}>{t}%</option>)}
            </select>
          </div>
          <button style={{ ...S.ghost, padding: "9px 14px" }} onClick={() => this.setState({ page: 1 }, this.loadData)}>↻ Refresh</button>
        </div>

        {this.testTo() && (
          <div style={{ ...S.notice(true), background: "#fffbeb", color: "#b45309", border: "1px solid #fde68a" }}>
            🧪 TEST MODE — every single/bulk WhatsApp & SMS from this screen goes to <b>{this.testTo()}</b>, NOT to parents.
            Clear the box to send real messages. (The “Notify Students” campaign modal ignores this and always sends to real parents.)
          </div>
        )}

        {notice && <div style={S.notice(notice.ok)}>{notice.msg}</div>}
        {error && <div style={S.notice(false)}>⚠ {error}</div>}

        {/* KPIs */}
        <div style={S.kpiRow}>
          {KPIS.map((k) => (
            <div key={k.l} style={{ ...S.kpi, borderTopColor: k.c }}>
              <div style={{ ...S.kpiVal, color: k.c }}>{k.v}</div>
              <div style={S.kpiLbl}>{k.l}</div>
            </div>
          ))}
        </div>
        {noData > 0 && (
          <div style={{ fontSize: 12, color: "#94a3b8", margin: "-8px 0 14px 2px" }}>
            ℹ {noData} student{noData !== 1 ? "s" : ""} excluded — no attendance recorded this month (section not yet on a device / not marked).
          </div>
        )}

        {/* quick actions */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
          <button disabled={!branch} style={S.btn("#0ea5e9")} onClick={() => this.setState({ notifyModal: true })}>
            <i className="fas fa-bell" /> Notify Students — Absentees ({absentToday.length})
          </button>
          {/* Notify defaulters — hidden per request.
          <button disabled={sending} style={S.btn("#dc2626")} onClick={() => this.sendWhatsApp(defaulterSet, (s) => `${s.pct}% attendance — below ${threshold}% this month`)}>
            <i className="fab fa-whatsapp" /> Notify defaulters ({defaulterSet.length})
          </button>
          */}
          <button style={S.ghost} onClick={() => this.exportDefaulters(defaulterSet)}>⬇ Export defaulters</button>
          {sending && <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#64748b", fontSize: 13 }}><Spinner animation="border" size="sm" /> sending…</span>}
        </div>

        {/* view tabs */}
        <div style={S.tabs}>
          {[
            { k: "board", l: "🎯 Defaulters & Eligibility" },
            { k: "heatmap", l: "🔥 Attendance Heatmap" },
            { k: "risk", l: "⚠️ At-Risk Early Warning" },
            { k: "analysis", l: "📨 Message Analysis" },
          ].map((t) => (
            <button
              key={t.k}
              style={{ ...S.tab, ...(view === t.k ? S.tabOn : {}) }}
              onClick={() => {
                // Analysis reads campaigns (independent of attendance), so load on entry.
                this.setState({ view: t.k, page: 1 }, () => { if (t.k === "analysis") this.loadCampaigns(); });
              }}
            >{t.l}</button>
          ))}
        </div>

        {/* Message Analysis is independent of the attendance load — render it before the
            "no attendance data" guard so it works even when the month has no marks yet. */}
        {view === "analysis" ? (
          this.renderAnalysis()
        ) : loading ? (
          <div style={{ textAlign: "center", padding: 50 }}><Spinner animation="border" /><div style={{ marginTop: 10, color: "#64748b" }}>Loading attendance…</div></div>
        ) : students.length === 0 ? (
          <div style={{ textAlign: "center", padding: 50, color: "#94a3b8" }}>📋 No attendance data for this branch/month.</div>
        ) : view === "board" ? (
          this.renderBoard(defaulters)
        ) : view === "heatmap" ? (
          this.renderHeatmap()
        ) : (
          this.renderRisk(atRisk)
        )}

        {this.renderTemplateModal()}
        {this.state.notifyStudent && (
          <NotifyStudentModal
            show
            onHide={() => this.setState({ notifyStudent: null })}
            student={this.state.notifyStudent.student}
            channel={this.state.notifyStudent.channel}
            testNumber={this.testTo()}
            month={this.state.month}
          />
        )}

        {(() => {
          const d = new Date();
          const todayStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          return (
            <NotifyAttendanceStudentsModal
              show={this.state.notifyModal}
              onHide={() => this.setState({ notifyModal: false })}
              students={absentToday}
              sectionName={(id) => this.sectionName(id)}
              branchId={branch}
              date={todayStr}
              dateLabel={d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
              testNumber={this.testTo()}
            />
          );
        })()}
      </div>
    );
  }

  // Template picker + editable params. Shown when an admin alerts at-risk /
  // defaulter parents and the institute has approved WhatsApp templates.
  renderTemplateModal() {
    const { tplModal, waTemplates, sending } = this.state;
    if (!tplModal) return null;
    const tpl = waTemplates.find((t) => String(t.id) === String(tplModal.templateId));
    const recipientCount = tplModal.rows.length;
    // Preview body with the edited params substituted into {{n}} placeholders.
    const preview = tpl
      ? (tpl.body || "").replace(/\{\{(\d+)\}\}/g, (_, n) => {
          const v = tplModal.params[Number(n) - 1];
          return v != null && v !== "" ? v : `{{${n}}}`;
        })
      : "";
    return (
      <Modal show centered onHide={() => this.setState({ tplModal: null })}>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16, fontWeight: 800, display: "flex", alignItems: "center", gap: 8 }}>
            <i className="fab fa-whatsapp" style={{ color: "#25D366" }} /> Send WhatsApp Alert
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ fontSize: 13, color: "#64748b", marginBottom: 12 }}>
            Alerting <b>{recipientCount}</b> parent{recipientCount === 1 ? "" : "s"}. The first
            field is personalised per student automatically.
          </div>
          {this.testTo() && (
            <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 12px", fontSize: 12.5, color: "#b45309", fontWeight: 700, marginBottom: 12 }}>
              🧪 TEST MODE — this will be delivered to {this.testTo()}, not to the parent{recipientCount === 1 ? "" : "s"}.
            </div>
          )}

          <label style={S.lbl}>TEMPLATE</label>
          <select
            style={{ ...S.sel, width: "100%", minWidth: 0, marginBottom: 14 }}
            value={tplModal.templateId || ""}
            onChange={(e) => this.selectTemplate(e.target.value)}
          >
            {waTemplates.map((t) => (
              <option key={t.id} value={t.id}>{t.name} ({t.lang})</option>
            ))}
          </select>

          {tpl && tpl.paramCount > 0 && (
            <>
              <label style={S.lbl}>PARAMETERS</label>
              {tplModal.params.map((p, i) => (
                <div key={i} style={{ marginBottom: 8 }}>
                  <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700 }}>{`{{${i + 1}}}`}{i === 0 ? " · per-student name" : ""}</span>
                  <input
                    style={{ ...S.sel, width: "100%", minWidth: 0 }}
                    value={p}
                    disabled={i === 0}
                    placeholder={`Value for {{${i + 1}}}`}
                    onChange={(e) => this.updateTplParam(i, e.target.value)}
                  />
                </div>
              ))}
            </>
          )}

          <label style={{ ...S.lbl, marginTop: 8 }}>PREVIEW</label>
          <div style={{ background: "#dcf8c6", borderRadius: 10, padding: "10px 12px", fontSize: 13, color: "#1e293b", whiteSpace: "pre-wrap", minHeight: 40 }}>
            {preview || <span style={{ color: "#94a3b8" }}>No body preview for this template.</span>}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => this.setState({ tplModal: null })}>Cancel</Button>
          <Button variant="success" disabled={sending || !tpl} onClick={this.confirmTemplateSend}>
            <i className="fab fa-whatsapp" /> Send to {recipientCount}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // ── pagination (client-side, shared by the Defaulters & At-Risk tables) ──
  // The current page is clamped to the row count, so a shrinking result set
  // (new search / filter) can never leave the user stranded on an empty page.
  pageOf = (total) => {
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    return Math.min(this.state.page, pages);
  };

  pageSlice = (rows) => {
    const page = this.pageOf(rows.length);
    const offset = (page - 1) * PAGE_SIZE;
    return { rows: rows.slice(offset, offset + PAGE_SIZE), offset };
  };

  renderPager(total) {
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (pages <= 1) return null;
    const page = this.pageOf(total);
    const btn = (disabled) => ({ ...S.ghost, opacity: disabled ? 0.4 : 1, cursor: disabled ? "not-allowed" : "pointer" });
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "#64748b" }}>
          Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
        </span>
        <button style={btn(page <= 1)} disabled={page <= 1} onClick={() => this.setState({ page: page - 1 })}>‹ Prev</button>
        <span style={{ fontSize: 12, fontWeight: 800, color: "#1e293b" }}>Page {page} / {pages}</span>
        <button style={btn(page >= pages)} disabled={page >= pages} onClick={() => this.setState({ page: page + 1 })}>Next ›</button>
      </div>
    );
  }

  renderBoard(defaulters) {
    const { search, threshold } = this.state;
    const searching = !!search.trim();
    const { rows: pageRows, offset } = this.pageSlice(defaulters);
    return (
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
          <div style={{ fontWeight: 800, color: "#1e293b" }}>
            {searching ? "Search — all students" : `Defaulters — below ${threshold}%`}{" "}
            <span style={{ color: "#94a3b8", fontWeight: 600 }}>({defaulters.length})</span>
          </div>
          <input style={{ ...S.sel, minWidth: 220 }} placeholder="Search name / admission / roll…" value={search} onChange={(e) => this.setState({ search: e.target.value, page: 1 })} />
        </div>
        {defaulters.length === 0 ? (
          searching
            ? <div style={{ textAlign: "center", padding: 30, color: "#94a3b8", fontWeight: 700 }}>No students match “{search.trim()}”.</div>
            : <div style={{ textAlign: "center", padding: 30, color: "#15803d", fontWeight: 700 }}>🎉 No defaulters below {threshold}% this month.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
              <thead><tr>
                {["#", "ROLL", "STUDENT", "CLASS · SECTION", "DAYS", "ATTENDANCE", "STATUS", "PARENT"].map((h) => <th key={h} style={S.th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {pageRows.map((s, i) => {
                  const st = statusOf(s.pct);
                  const phone = s.father_phone || s.mother_phone || s.student_mobile;
                  return (
                    <tr key={s.student_id}>
                      <td style={S.td}>{offset + i + 1}</td>
                      <td style={S.td}>{s.roll_no || "-"}</td>
                      <td style={{ ...S.td, fontWeight: 700 }}>{s.student_name}<div style={{ fontSize: 10, color: "#94a3b8" }}>Adm {s.admission_no}</div></td>
                      <td style={S.td}>{this.className(s.class_id)} · {this.sectionName(s.section_id)}</td>
                      <td style={S.td}>{s.total_present}/{s.total_working_days}</td>
                      <td style={{ ...S.td, fontWeight: 800, color: st.c }}>{s.pct}%</td>
                      <td style={S.td}><span style={S.pill(st.c, st.bg)}>{st.label}</span></td>
                      <td style={S.td}>
                        {phone ? (
                          <span style={{ display: "inline-flex", gap: 6 }}>
                            <button style={S.btn("#16a34a")} title="Send WhatsApp to parent" onClick={() => this.openNotifyStudent(s, "whatsapp")}><i className="fab fa-whatsapp" /></button>
                            <a href={`tel:${phone}`} style={{ ...S.ghost, textDecoration: "none" }}>📞</a>
                          </span>
                        ) : <span style={{ color: "#cbd5e1" }}>no phone</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {this.renderPager(defaulters.length)}
          </div>
        )}
      </div>
    );
  }

  renderHeatmap() {
    const { month } = this.state;
    const students = this.state.students.filter((s) => (s.total_working_days || 0) > 0);
    const [y, m] = month.split("-").map(Number);
    const daysInMonth = new Date(y, m, 0).getDate();
    const todayD = (new Date().getFullYear() === y && new Date().getMonth() + 1 === m) ? new Date().getDate() : -1;

    // group students by section
    const secMap = {};
    students.forEach((s) => { const k = `${s.class_id}|${s.section_id}`; (secMap[k] = secMap[k] || []).push(s); });
    const sections = Object.keys(secMap).sort();
    if (!sections.length)
      return <div style={S.card}><div style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}>No section has attendance recorded this month.</div></div>;

    // per section per day → % present
    const cell = (list, d) => {
      const key = `${String(d).padStart(2, "0")}-${String(m).padStart(2, "0")}`;
      let p = 0, a = 0;
      list.forEach((s) => { const rec = (s.days || []).find((x) => x.day === key); if (!rec) return; if (rec.status === "P") p++; else if (rec.status === "A") a++; });
      return p + a > 0 ? Math.round((p / (p + a)) * 100) : null;
    };

    return (
      <div style={S.card}>
        <div style={{ fontWeight: 800, color: "#1e293b", marginBottom: 4 }}>Attendance Heatmap — Class · Section × Day</div>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>Each cell = % present that day. Green = high, red = low, grey = no school.</div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={{ ...S.th, position: "sticky", left: 0, background: "#fff", minWidth: 150 }}>SECTION</th>
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => (
                <th key={d} style={{ ...S.th, textAlign: "center", padding: "6px 4px", color: d === todayD ? "#1d4ed8" : "#94a3b8" }}>{d}</th>
              ))}
              <th style={{ ...S.th, textAlign: "center" }}>AVG</th>
            </tr></thead>
            <tbody>
              {sections.map((k) => {
                const list = secMap[k];
                const [cid, sid] = k.split("|");
                const vals = Array.from({ length: daysInMonth }, (_, i) => cell(list, i + 1));
                const real = vals.filter((v) => v != null);
                const avg = real.length ? Math.round(real.reduce((a, b) => a + b, 0) / real.length) : 0;
                return (
                  <tr key={k}>
                    <td style={{ ...S.td, fontWeight: 700, position: "sticky", left: 0, background: "#fff", whiteSpace: "nowrap" }}>{this.className(cid)} · {this.sectionName(sid)}</td>
                    {vals.map((v, i) => (
                      <td key={i} title={v == null ? "no school" : `${i + 1}: ${v}%`} style={{ padding: 0, textAlign: "center", border: (i + 1) === todayD ? "2px solid #2563eb" : "1px solid #fff" }}>
                        <div style={{ background: heatColor(v), width: 26, height: 26, margin: "1px auto", borderRadius: 4, color: v != null && v < 60 ? "#fff" : "#0f172a", fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{v != null ? v : ""}</div>
                      </td>
                    ))}
                    <td style={{ ...S.td, textAlign: "center", fontWeight: 800, color: heatColor(avg) }}>{avg}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Message Analysis: SMS + WhatsApp attendance-alert send details, per campaign.
  renderAnalysis() {
    const { campaigns, campaignsLoading, campaignChannel, drillCampaign, drillRows, drillLoading,
            campaignSearch, campaignPage, drillSearch, drillPage } = this.state;
    const fmtWhen = (t) => {
      const n = parseInt(t); if (!n) return "—";
      return new Date((n > 1e12 ? n : n * 1000)).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    };
    const PAGE = 10;
    const inputStyle = { padding: "6px 10px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13, outline: "none", minWidth: 180 };
    // Campaigns: channel filter + name search + client-side pagination.
    const q = String(campaignSearch || "").trim().toLowerCase();
    const filteredAll = (campaignChannel === "all" ? campaigns : campaigns.filter((c) => c.channel === campaignChannel))
      .filter((c) => !q || String(c.campaign_name || "").toLowerCase().includes(q));
    const cTotalPages = Math.max(1, Math.ceil(filteredAll.length / PAGE));
    const cpage = Math.min(Math.max(1, campaignPage), cTotalPages);
    const filtered = filteredAll.slice((cpage - 1) * PAGE, cpage * PAGE);
    // Recipient drill-down: search (name / mobile / status) + pagination.
    const dq = String(drillSearch || "").trim().toLowerCase();
    const drillAll = drillRows.filter((r) => !dq
      || String(r.name || "").toLowerCase().includes(dq)
      || String(r.mobile || "").toLowerCase().includes(dq)
      || String(r.status || "").toLowerCase().includes(dq));
    const dTotalPages = Math.max(1, Math.ceil(drillAll.length / PAGE));
    const dpage = Math.min(Math.max(1, drillPage), dTotalPages);
    const drillPageRows = drillAll.slice((dpage - 1) * PAGE, dpage * PAGE);
    // Channel totals for the summary strip.
    const sum = (list, k) => list.reduce((a, c) => a + (parseInt(c[k]) || 0), 0);
    const smsList = campaigns.filter((c) => c.channel === "sms");
    const waList = campaigns.filter((c) => c.channel === "whatsapp");
    const chStat = (label, list, accent) => (
      <div style={{ flex: 1, minWidth: 190, border: "1.5px solid #e5e7eb", borderRadius: 12, padding: "12px 14px" }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: accent, marginBottom: 6 }}>{label}</div>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12.5, color: "#475569" }}>
          <span><b style={{ color: "#1e293b" }}>{list.length}</b> campaigns</span>
          <span>sent <b style={{ color: "#15803d" }}>{sum(list, "sent")}</b></span>
          <span>failed <b style={{ color: "#dc2626" }}>{sum(list, "failed")}</b></span>
          {label.includes("WhatsApp")
            ? <><span>delivered <b>{sum(list, "delivered")}</b></span><span>read <b>{sum(list, "read")}</b></span><span>scheduled <b style={{ color: "#b45309" }}>{sum(list, "scheduled")}</b></span></>
            : <span>scheduled <b style={{ color: "#b45309" }}>{sum(list, "scheduled")}</b></span>}
        </div>
      </div>
    );
    const statCell = (n, c) => <span style={{ fontWeight: 700, color: (parseInt(n) || 0) ? c : "#cbd5e1" }}>{parseInt(n) || 0}</span>;

    return (
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
          <div style={{ fontWeight: 800, color: "#1e293b" }}>📨 Message Analysis <span style={{ color: "#94a3b8", fontWeight: 600 }}>— attendance alerts sent to parents</span></div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <input value={campaignSearch} onChange={(e) => this.setState({ campaignSearch: e.target.value, campaignPage: 1 })} placeholder="Search campaign…" style={inputStyle} />
            {[{ k: "all", l: "All" }, { k: "sms", l: "SMS" }, { k: "whatsapp", l: "WhatsApp" }].map((f) => (
              <button key={f.k} style={{ ...S.tab, padding: "5px 12px", ...(campaignChannel === f.k ? S.tabOn : {}) }} onClick={() => this.setState({ campaignChannel: f.k, campaignPage: 1 })}>{f.l}</button>
            ))}
            <button style={{ ...S.ghost, padding: "6px 12px" }} onClick={this.loadCampaigns} disabled={campaignsLoading}>↻</button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
          {chStat("📱 SMS (sms_outbox)", smsList, "#0ea5e9")}
          {chStat("💬 WhatsApp (outbond_campaign)", waList, "#16a34a")}
        </div>

        {campaignsLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}><Spinner animation="border" size="sm" /> <span style={{ color: "#64748b", marginLeft: 8 }}>Loading campaigns…</span></div>
        ) : filteredAll.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>{q ? "No campaigns match your search." : `No ${campaignChannel === "all" ? "" : campaignChannel.toUpperCase() + " "}attendance messages sent yet.`}</div>
        ) : (
          <>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={S.th}>Campaign</th><th style={S.th}>Channel</th><th style={S.th}>When</th>
                <th style={S.th}>Attempted</th><th style={S.th}>Sent</th><th style={S.th}>Delivered</th>
                <th style={S.th}>Read</th><th style={S.th}>Failed</th><th style={S.th}>Scheduled</th><th style={S.th}>Cancelled</th><th style={S.th}></th>
              </tr></thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={`${c.channel}-${c.id}`}>
                    <td style={S.td}>{c.campaign_name}</td>
                    <td style={S.td}><span style={S.pill(c.channel === "sms" ? "#0369a1" : "#15803d", c.channel === "sms" ? "#e0f2fe" : "#f0fdf4")}>{c.channel === "sms" ? "SMS" : "WhatsApp"}</span></td>
                    <td style={{ ...S.td, color: "#64748b" }}>{fmtWhen(c.inserted_time)}</td>
                    <td style={S.td}>{statCell(c.attempted, "#1e293b")}</td>
                    <td style={S.td}>{statCell(c.sent, "#15803d")}</td>
                    <td style={S.td}>{c.channel === "whatsapp" ? statCell(c.delivered, "#0369a1") : <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                    <td style={S.td}>{c.channel === "whatsapp" ? statCell(c.read, "#7c3aed") : <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                    <td style={S.td}>{statCell(c.failed, "#dc2626")}</td>
                    {/* WhatsApp now has a real Scheduled count too (wa_outbox, staged at 09:00
                        for the 09:30 send) — no more channel-based "—" suppression. */}
                    <td style={S.td}>{statCell(c.scheduled, "#b45309")}</td>
                    <td style={S.td}>{c.channel === "sms" ? statCell(c.cancelled, "#64748b") : <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                    <td style={S.td}><button style={S.btn("#4f46e5")} onClick={() => this.openDrill(c)}>View</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredAll.length > PAGE && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, flexWrap: "wrap", gap: 8 }}>
              <span style={{ fontSize: 12.5, color: "#64748b" }}>Showing {(cpage - 1) * PAGE + 1}–{Math.min(cpage * PAGE, filteredAll.length)} of {filteredAll.length}</span>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <button style={{ ...S.ghost, padding: "4px 10px" }} disabled={cpage <= 1} onClick={() => this.setState({ campaignPage: cpage - 1 })}>‹ Prev</button>
                <span style={{ fontSize: 12.5, color: "#475569" }}>Page {cpage} / {cTotalPages}</span>
                <button style={{ ...S.ghost, padding: "4px 10px" }} disabled={cpage >= cTotalPages} onClick={() => this.setState({ campaignPage: cpage + 1 })}>Next ›</button>
              </div>
            </div>
          )}
          </>
        )}

        {/* Recipient drill-down */}
        <Modal show={!!drillCampaign} onHide={() => this.setState({ drillCampaign: null })} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: 16 }}>
              {drillCampaign ? `${drillCampaign.campaign_name} · ${drillCampaign.channel === "sms" ? "SMS" : "WhatsApp"}` : ""}
              <span style={{ color: "#94a3b8", fontWeight: 400, fontSize: 13 }}> — recipients</span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
            {drillLoading ? (
              <div style={{ textAlign: "center", padding: 30 }}><Spinner animation="border" size="sm" /></div>
            ) : drillRows.length === 0 ? (
              <div style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}>No recipient details.</div>
            ) : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
                  <span style={{ fontSize: 12.5, color: "#64748b" }}>{drillAll.length} recipient{drillAll.length === 1 ? "" : "s"}</span>
                  <input value={drillSearch} onChange={(e) => this.setState({ drillSearch: e.target.value, drillPage: 1 })} placeholder="Search name / mobile / status…" style={inputStyle} />
                </div>
                {drillAll.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}>No matching recipients.</div>
                ) : (
                  <>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead><tr><th style={S.th}>#</th><th style={S.th}>Name</th><th style={S.th}>Mobile</th><th style={S.th}>Status</th><th style={S.th}>Sent At</th><th style={S.th}>Message</th></tr></thead>
                      <tbody>
                        {drillPageRows.map((r, i) => {
                          const st = String(r.status || "").toLowerCase();
                          const sc = st === "sent" || st === "delivered" || st === "read" ? "#15803d"
                            : st === "failed" ? "#dc2626" : st === "cancelled" ? "#64748b" : "#b45309";
                          return (
                            <tr key={i}>
                              <td style={S.td}>{(dpage - 1) * PAGE + i + 1}</td>
                              <td style={S.td}>{r.name || "—"}</td>
                              <td style={S.td}>{r.mobile || "—"}</td>
                              <td style={S.td}><span style={S.pill(sc, "#f8fafc")}>{r.status || "—"}</span></td>
                              <td style={{ ...S.td, color: "#64748b" }}>{fmtWhen(r.sent_at)}</td>
                              <td style={{ ...S.td, fontSize: 12, color: "#475569", maxWidth: 320, whiteSpace: "pre-wrap" }}>
                                {r.error ? <span style={{ color: "#dc2626" }}>{r.error}</span> : (r.message || "—")}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {drillAll.length > PAGE && (
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, flexWrap: "wrap", gap: 8 }}>
                        <span style={{ fontSize: 12.5, color: "#64748b" }}>Showing {(dpage - 1) * PAGE + 1}–{Math.min(dpage * PAGE, drillAll.length)} of {drillAll.length}</span>
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <button style={{ ...S.ghost, padding: "4px 10px" }} disabled={dpage <= 1} onClick={() => this.setState({ drillPage: dpage - 1 })}>‹ Prev</button>
                          <span style={{ fontSize: 12.5, color: "#475569" }}>Page {dpage} / {dTotalPages}</span>
                          <button style={{ ...S.ghost, padding: "4px 10px" }} disabled={dpage >= dTotalPages} onClick={() => this.setState({ drillPage: dpage + 1 })}>Next ›</button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </Modal.Body>
        </Modal>
      </div>
    );
  }

  renderRisk(atRisk) {
    const { search } = this.state;
    const searching = !!search.trim();
    const { rows: pageRows } = this.pageSlice(atRisk);
    return (
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4, flexWrap: "wrap", gap: 10 }}>
          <div style={{ fontWeight: 800, color: "#1e293b" }}>At-Risk Early Warning <span style={{ color: "#94a3b8", fontWeight: 600 }}>({atRisk.length})</span></div>
          <input style={{ ...S.sel, minWidth: 220 }} placeholder="Search name / admission / roll…" value={search} onChange={(e) => this.setState({ search: e.target.value, page: 1 })} />
        </div>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>Students below {ELIGIBLE_MIN}% <b>or</b> with 3+ recent consecutive absences — intervene early.</div>
        {atRisk.length === 0 ? (
          searching
            ? <div style={{ textAlign: "center", padding: 30, color: "#94a3b8", fontWeight: 700 }}>No at-risk students match “{search.trim()}”.</div>
            : <div style={{ textAlign: "center", padding: 30, color: "#15803d", fontWeight: 700 }}>🎉 No at-risk students this month.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
              <thead><tr>{["ROLL", "STUDENT", "CLASS · SECTION", "ATTENDANCE", "CONSEC. ABSENT", "RISK", "PARENT"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {pageRows.map((s) => {
                  const high = s.pct < CONDONABLE_MIN || s.consec >= 5;
                  const phone = s.father_phone || s.mother_phone || s.student_mobile;
                  return (
                    <tr key={s.student_id}>
                      <td style={S.td}>{s.roll_no || "-"}</td>
                      <td style={{ ...S.td, fontWeight: 700 }}>{s.student_name}</td>
                      <td style={S.td}>{this.className(s.class_id)} · {this.sectionName(s.section_id)}</td>
                      <td style={{ ...S.td, fontWeight: 800, color: statusOf(s.pct).c }}>{s.pct}%</td>
                      <td style={S.td}>{s.consec >= 1 ? <span style={S.pill("#dc2626", "#fef2f2")}>{s.consec} days</span> : "-"}</td>
                      <td style={S.td}><span style={S.pill(high ? "#dc2626" : "#b45309", high ? "#fef2f2" : "#fffbeb")}>{high ? "High" : "Watch"}</span></td>
                      <td style={S.td}>{phone ? (
                        <span style={{ display: "inline-flex", gap: 6 }}>
                          <button style={S.btn("#16a34a")} onClick={() => this.openNotifyStudent(s, "whatsapp")}><i className="fab fa-whatsapp" /> Alert</button>
                        </span>
                      ) : <span style={{ color: "#cbd5e1" }}>no phone</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {this.renderPager(atRisk.length)}
          </div>
        )}
      </div>
    );
  }
}

export default withApollo(AttendanceCommandCenter);
